import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { api } from '../services/api';
import {
  ProfitChartApiResponse,
  SubscriptionProfitChartApiResponse,
} from '../../screens/Detail/Charts/types';
import { applyDemoCasesToChartDataBuilder } from './demoEffects/chartData';

export interface ChartDataState {
  data: Record<number, ProfitChartApiResponse>;
}

const initialState: ChartDataState = {
  data: {},
};

const chartDataSlice = createSlice({
  name: 'chartData',
  initialState,
  reducers: {
    hydrateChartData: (
      state,
      action: PayloadAction<Record<number, SubscriptionProfitChartApiResponse>>,
    ) => {
      state.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Demo-mode reducer cases live in their own file so this slice has no
    // direct knowledge of demo mode.
    applyDemoCasesToChartDataBuilder(builder);

    builder.addMatcher(
      api.endpoints.getSubscriptionDetails.matchFulfilled,
      (state, { payload }) => {
        if (payload?.chartData) {
          state.data[payload.id] = payload.chartData;
        }
      },
    );

    // The Overview grid loads every card's chart via the batched endpoint.
    // Populate the chart cache directly from it (without touching the
    // subscription slice, which would re-sort the grid and loop the query).
    builder.addMatcher(
      api.endpoints.getSubscriptionsDetailsBatch.matchFulfilled,
      (state, { payload }) => {
        (payload?.subscriptionDetails ?? []).forEach((detail) => {
          if (detail?.chartData) {
            state.data[detail.id] = detail.chartData;
          }
        });
      },
    );
  },
});

export const { hydrateChartData } = chartDataSlice.actions;

export const selectChartData = (subscriptionId: number) => (state: RootState) =>
  state.chartData.data[subscriptionId];

export const selectAllChartData = (state: RootState) => state.chartData.data;

export default chartDataSlice.reducer;
